import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineHooks } from '@dopomogai/chat-react';
import { Card } from '@dopomogai/ui';
import { StudioWidget } from '@dopomogai/widget-presets';

import {
  type OnboardingAssistantContext,
  type OnboardingAssistantFieldPatch,
  type OnboardingAssistantSyncPatchResult,
  ONBOARDING_ASSISTANT_SYNC_FIELDS,
  pickOnboardingAssistantFieldPatch,
} from '../types/onboardingAssistant';
import {
  DEFAULT_ONBOARDING_SESSION_STORAGE_KEY,
  composeTurnPromptOverrides,
  getOrCreateOnboardingSessionId,
  resolveAssistantRuntime,
  type AssistantRuntimeResolveRequest,
  type AssistantRuntimeResolveResponse,
} from '../utils/assistantRuntime';

const DEFAULT_BUILDER_ASSISTANT_AGENT_ID = 'builder-assistant-v1';
const DEFAULT_BUILDER_ASSISTANT_PROFILE_ID = 'onboarding_master';
const BUILDER_ONBOARDING_PERSIST_NAMESPACE = 'agent-builder:onboarding-step2';
const BUILDER_ONBOARDING_SESSION_STORAGE_KEY =
  DEFAULT_ONBOARDING_SESSION_STORAGE_KEY;
const STEP2_SYNC_HOOK_NAME = 'sync_onboarding_fields';

const STEP2_SYNC_FIELD_SCHEMA = Object.fromEntries(
  ONBOARDING_ASSISTANT_SYNC_FIELDS.map((field) => [field, { type: 'string' }]),
);

const STEP2_SYNC_MANIFEST = {
  version: '2026-03-09',
  pageId: 'agent_builder_onboarding_step2',
  allow: [STEP2_SYNC_HOOK_NAME],
  hooks: [
    {
      name: STEP2_SYNC_HOOK_NAME,
      description:
        'Mirror approved onboarding content into the Agent Builder form. Update one or many fields at once using the fields object.',
      paramsSchema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          fields: {
            type: 'object',
            additionalProperties: false,
            properties: STEP2_SYNC_FIELD_SCHEMA,
          },
          confirmOverwrite: {
            type: 'boolean',
          },
        },
        required: ['fields'],
      },
      examples: [
        {
          name: STEP2_SYNC_HOOK_NAME,
          params: {
            fields: {
              personalityPrompt:
                'You are a calm, proactive onboarding concierge who explains next steps in simple language.',
              basicKnowledge:
                'We are a B2B AI studio focused on agent design, implementation, and launch readiness.',
            },
          },
        },
      ],
      safety: {
        confirm: true,
        note:
          'Set confirmOverwrite to true only when the user explicitly asks you to replace or overwrite a manually edited field.',
      },
    },
  ],
  prompt: [
    'You are helping with Step 2 of the Agent Builder onboarding flow.',
    `When the user clearly provides or approves content, call frontend_actions with the hook "${STEP2_SYNC_HOOK_NAME}".`,
    'Pass all intended updates in a single fields object so the form stays in sync.',
    'Never overwrite a manually edited field unless the user explicitly asks you to replace it; only then set confirmOverwrite to true.',
    'For websiteDomain use comma-separated domains without protocol or path.',
    'For notificationEmail use comma-separated email addresses.',
    'If the request is ambiguous, ask one short clarifying question instead of guessing.',
  ].join('\n'),
};

interface OnboardingAssistantPanelProps {
  agentName?: string;
  assistantContext: OnboardingAssistantContext;
  onApplyFieldPatch: (
    patch: OnboardingAssistantFieldPatch,
    options?: { confirmOverwrite?: boolean },
  ) => OnboardingAssistantSyncPatchResult;
}

function resolveRuntimeEnv(key: string): string | undefined {
  try {
    if (typeof process !== 'undefined' && (process as any).env?.[key]) {
      return (process as any).env[key];
    }
  } catch {}

  try {
    if (
      typeof window !== 'undefined' &&
      (window as any).__DOPOMOGAI_ENV__?.[key]
    ) {
      return (window as any).__DOPOMOGAI_ENV__[key];
    }
  } catch {}

  return undefined;
}

function resolvePlatformApiBaseUrl(): string | undefined {
  return (
    resolveRuntimeEnv('VITE_API_URL') ??
    (typeof window !== 'undefined'
      ? (window as any).__API_BASE_URL__
      : undefined) ??
    (typeof window !== 'undefined'
      ? (window as any).__DOPOMOGAI_API_BASE__
      : undefined)
  );
}

function resolveBuilderAssistantAgentId(): string {
  return (
    resolveRuntimeEnv('VITE_AGENT_BUILDER_ASSISTANT_ID') ??
    DEFAULT_BUILDER_ASSISTANT_AGENT_ID
  );
}

function resolveBuilderAssistantProfileId(): string {
  return (
    resolveRuntimeEnv('VITE_AGENT_BUILDER_ASSISTANT_PROFILE_ID') ??
    DEFAULT_BUILDER_ASSISTANT_PROFILE_ID
  );
}

export const OnboardingAssistantPanel: React.FC<OnboardingAssistantPanelProps> = ({
  agentName,
  assistantContext,
  onApplyFieldPatch,
}) => {
  const apiBaseUrl = resolvePlatformApiBaseUrl();
  const assistantAgentId = resolveBuilderAssistantAgentId();
  const assistantProfileId = resolveBuilderAssistantProfileId();
  const onboardingSessionId = useMemo(
    () =>
      getOrCreateOnboardingSessionId(
        BUILDER_ONBOARDING_SESSION_STORAGE_KEY,
      ),
    [],
  );
  const resolvedAgentName = agentName?.trim() || 'your agent';

  const assistantContextRef = useRef(assistantContext);
  const applyFieldPatchRef = useRef(onApplyFieldPatch);
  const [resolvedRuntime, setResolvedRuntime] =
    useState<AssistantRuntimeResolveResponse | null>(null);

  useEffect(() => {
    assistantContextRef.current = assistantContext;
  }, [assistantContext]);

  useEffect(() => {
    applyFieldPatchRef.current = onApplyFieldPatch;
  }, [onApplyFieldPatch]);

  useEffect(() => {
    if (!apiBaseUrl) {
      setResolvedRuntime(null);
      return;
    }

    const controller = new AbortController();
    const payload: AssistantRuntimeResolveRequest = {
      assistant_profile_id: assistantProfileId,
      subject_agent_id: null,
      workspace_mode: 'assistant',
      interaction_mode: 'test',
      flow_step: 'step2',
      context: {
        onboarding_form: {
          form_id: assistantContext.onboarding_form.form_id,
          surface: assistantContext.onboarding_form.surface,
          active_step_id: assistantContext.onboarding_form.active_step_id,
        },
        onboarding_session_id: onboardingSessionId,
      },
    };

    void resolveAssistantRuntime(payload, controller.signal).then((nextRuntime) => {
      if (!controller.signal.aborted) {
        setResolvedRuntime(nextRuntime);
      }
    });

    return () => {
      controller.abort();
    };
  }, [
    apiBaseUrl,
    assistantProfileId,
    onboardingSessionId,
    assistantContext.onboarding_form.form_id,
    assistantContext.onboarding_form.surface,
    assistantContext.onboarding_form.active_step_id,
  ]);

  const onboardingHooks = useMemo(
    () =>
      defineHooks(
        STEP2_SYNC_MANIFEST,
        {
          [STEP2_SYNC_HOOK_NAME]: (params: {
            fields?: unknown;
            confirmOverwrite?: boolean;
          }) => {
            const patch = pickOnboardingAssistantFieldPatch(params?.fields);
            return applyFieldPatchRef.current(patch, {
              confirmOverwrite: params?.confirmOverwrite === true,
            });
          },
        },
        {
          pageId: 'agent_builder_onboarding_step2',
          extraContext: () => assistantContextRef.current,
        },
      ),
    [],
  );

  const resolvedAssistantAgentId =
    resolvedRuntime?.runtime_agent_id ?? assistantAgentId;
  const resolvedSubjectContextKey =
    resolvedRuntime?.subject_context_key ?? `onboarding:${onboardingSessionId}`;

  const mergedRequestContext = useCallback(() => {
    const baseContext = onboardingHooks.overrides.perTurnContext?.();
    const safeBaseContext =
      baseContext && typeof baseContext === 'object' && !Array.isArray(baseContext)
        ? baseContext
        : {};

    return {
      ...safeBaseContext,
      assistant_profile_id:
        resolvedRuntime?.assistant_profile_id ?? assistantProfileId,
      runtime_agent_id: resolvedAssistantAgentId,
      subject_agent_id: resolvedRuntime?.subject_agent_id ?? null,
      subject_context_key: resolvedSubjectContextKey,
      onboarding_session_id: onboardingSessionId,
      scan_id: null,
    };
  }, [
    onboardingHooks.overrides.perTurnContext,
    resolvedRuntime,
    assistantProfileId,
    resolvedAssistantAgentId,
    resolvedSubjectContextKey,
    onboardingSessionId,
  ]);

  const mergedTurnPromptOverride = useCallback(() => {
    const runtimeTurnPrompt =
      resolvedRuntime?.prompt_config?.turn_prompt_override ?? undefined;
    const perTurnPrompt = onboardingHooks.overrides.perTurnPrompt?.();

    return composeTurnPromptOverrides(runtimeTurnPrompt, perTurnPrompt);
  }, [
    onboardingHooks.overrides.perTurnPrompt,
    resolvedRuntime?.prompt_config?.turn_prompt_override,
  ]);

  return (
    <Card title="Onboarding Assistant" headerBgColor="bg-violet-100" className="h-full">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Use the assistant to shape the personality and knowledge for{' '}
          {resolvedAgentName}. It sees the live onboarding form context and can
          draft directly into unlocked fields on the new widget runtime.
        </p>

        {apiBaseUrl ? (
          <div className="h-[34rem] overflow-hidden rounded-2xl border-2 border-black bg-white shadow-[4px_4px_0_0_#000]">
            <StudioWidget
              config={{
                agentId: resolvedAssistantAgentId,
                apiBaseUrl,
                authMode: 'WIDGET',
                profile: 'onboarding_step2',
                persistNamespace: BUILDER_ONBOARDING_PERSIST_NAMESPACE,
                allowClientTools: true,
                assistantProfileId:
                  resolvedRuntime?.assistant_profile_id ?? assistantProfileId,
                runtimeAgentId: resolvedAssistantAgentId,
                subjectAgentId: resolvedRuntime?.subject_agent_id ?? null,
                subjectContextKey: resolvedSubjectContextKey,
                onboardingSessionId,
                readinessSummary:
                  resolvedRuntime?.readiness_summary ?? undefined,
                tools: onboardingHooks.headlessTools,
                requestContext: mergedRequestContext,
                turnPromptOverride: mergedTurnPromptOverride,
              }}
              className="h-full"
            />
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Set `VITE_API_URL` to load the Step 2 onboarding assistant.
          </div>
        )}
      </div>
    </Card>
  );
};
